import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/components/molecules/pageLayout';
import { MaxContent } from '@/styles/globalStyles';
import { useTranslation } from '@/hooks/useTranslation';
import { QUERY_MY_PROJECT_ANALYSIS_REPORTS } from '@/graphql/queries/projectAnalysis';
import type { MyProjectAnalysisReport } from '@/graphql/types/projectAnalysis';
import { AppRouters } from '@/utils/constants/routers';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { dateFormat } from '@/utils/functions/dateFormat';
import { Text } from '@/components/atoms/text';
import { ProjectAnalysisListLoading } from './loading';

type MyProjectAnalysisReportsData = {
  myProjectAnalysisReports: MyProjectAnalysisReport[];
};

export default function ProjectAnalysisListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data, loading } = useQuery<MyProjectAnalysisReportsData>(
    QUERY_MY_PROJECT_ANALYSIS_REPORTS,
    {
      fetchPolicy: 'cache-first',
    }
  );

  const reports = data?.myProjectAnalysisReports ?? [];

  return (
    <PageLayout.Root
      contentPadding={{ vertical: 2, horizontal: 2 }}
      header={
        <PageLayout.Background>
          <PageLayout.Header
            title={t('page.projectAnalysis.projectAnalysis.title')}
            onBack={() => navigate(AppRouters.home)}
          />
        </PageLayout.Background>
      }
    >
      <MaxContent>
        <Text variant="body2" secondary sx={{ mb: 2 }}>
          {t('page.projectAnalysis.projectAnalysis.subtitle')}
        </Text>

        {loading ? (
          <ProjectAnalysisListLoading />
        ) : reports.length === 0 ? (
          <Text variant="body2">{t('page.projectAnalysis.projectAnalysis.empty')}</Text>
        ) : (
          <div className="rounded-md border border-slate-200 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('page.projectAnalysis.projectAnalysis.reportTitle')}</TableHead>
                  <TableHead>{t('page.projectAnalysis.projectAnalysis.filename')}</TableHead>
                  <TableHead>{t('page.projectAnalysis.projectAnalysis.publishedAt')}</TableHead>
                  <TableHead className="w-[120px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.title}</TableCell>
                    <TableCell>{r.original_filename}</TableCell>
                    <TableCell>{dateFormat(new Date(r.published_at))}</TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => navigate(`${AppRouters.projectAnalysis}/${r.id}`)}
                      >
                        {t('page.projectAnalysis.projectAnalysis.open')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </MaxContent>
    </PageLayout.Root>
  );
}
